export interface ColumnDescriptor {
  key: string
  header: string
  label: string
  sourceIndex: number
  preferenceAlias?: string
  defaultHidden?: boolean
}

function columnIdentityKey(header: string, occurrence: number): string {
  return JSON.stringify([header, occurrence])
}

export function describeColumns(headers: string[]): ColumnDescriptor[] {
  const totals = new Map<string, number>()
  for (const header of headers) totals.set(header, (totals.get(header) ?? 0) + 1)

  const occurrences = new Map<string, number>()
  return headers.map((header, sourceIndex) => {
    const occurrence = occurrences.get(header) ?? 0
    occurrences.set(header, occurrence + 1)
    const total = totals.get(header) ?? 1
    const baseLabel = header || 'Unnamed column'
    return {
      key: columnIdentityKey(header, occurrence),
      header,
      label: total > 1 ? `${baseLabel} (${occurrence + 1})` : baseLabel,
      sourceIndex,
    }
  })
}

export function descriptorPreferenceKeys(descriptor: ColumnDescriptor): string[] {
  return descriptor.preferenceAlias && descriptor.preferenceAlias !== descriptor.key
    ? [descriptor.key, descriptor.preferenceAlias]
    : [descriptor.key]
}

export function orderDescriptors(
  order: string[],
  descriptors: ColumnDescriptor[],
): ColumnDescriptor[] {
  const remaining = new Set(descriptors)
  const ordered: ColumnDescriptor[] = []
  const byPreferenceKey = new Map<string, ColumnDescriptor[]>()
  for (const descriptor of descriptors) {
    for (const key of descriptorPreferenceKeys(descriptor)) {
      const candidates = byPreferenceKey.get(key) ?? []
      candidates.push(descriptor)
      byPreferenceKey.set(key, candidates)
    }
  }
  for (const key of order) {
    const candidates = byPreferenceKey.get(key)
    while (candidates?.length && !remaining.has(candidates[0])) candidates.shift()
    const descriptor = candidates?.shift()
    if (!descriptor) continue
    ordered.push(descriptor)
    remaining.delete(descriptor)
  }
  for (const descriptor of descriptors) {
    if (remaining.has(descriptor)) ordered.push(descriptor)
  }
  return ordered
}

export function mergePreferenceGroup(
  persistedGroup: string[],
  visibleKeys: Set<string>,
  desiredGroup: string[],
): string[] {
  const remaining = [...desiredGroup]
  const merged: string[] = []
  let lastReplacement = -1

  for (const key of persistedGroup) {
    if (!visibleKeys.has(key)) {
      merged.push(key)
      continue
    }
    const replacement = remaining.shift()
    if (!replacement) continue
    merged.push(replacement)
    lastReplacement = merged.length - 1
  }

  const insertionIndex = lastReplacement >= 0 ? lastReplacement + 1 : merged.length
  merged.splice(insertionIndex, 0, ...remaining)
  return [...new Set(merged)]
}
